// NavbarAuthButtons.tsx
import {ButtonColor} from '@common/ui/buttons/get-shared-button-style';
import {useSettings} from '@common/core/settings/use-settings';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {Menu, MenuTrigger} from '@common/ui/navigation/menu/menu-trigger';
import {IconButton} from '@common/ui/buttons/icon-button';
import {PersonIcon} from '@common/icons/material/Person';
import {Item} from '@common/ui/forms/listbox/item';
import {Trans} from '@common/i18n/trans';
import {Link} from 'react-router-dom';
import {Button} from '@common/ui/buttons/button';
import {NavbarProps} from '@common/ui/navigation/navbar/navbar';
import {Fragment} from 'react';
import clsx from 'clsx';
import {useAuthModal} from '@common/auth/ui/auth-modal/auth-modal-context';

interface NavbarAuthButtonsProps {
  primaryButtonColor?: ButtonColor;
  navbarColor?: NavbarProps['color'];
}

export function NavbarAuthButtons({
  primaryButtonColor,
  navbarColor,
}: NavbarAuthButtonsProps) {
  if (!primaryButtonColor) {
    primaryButtonColor = navbarColor === 'primary' ? 'paper' : 'primary';
  }

  return (
    <Fragment>
      <MobileButtons />
      <DesktopButtons primaryButtonColor={primaryButtonColor} />
    </Fragment>
  );
}

/* ----------------------------- DESKTOP ---------------------------------- */
interface DesktopButtonsProps {
  primaryButtonColor: ButtonColor;
}
function DesktopButtons({primaryButtonColor}: DesktopButtonsProps) {
  const {registration} = useSettings();
  const authModal = useAuthModal();

  const handleLogin = (e: React.MouseEvent) => {
    if (authModal) {
      e.preventDefault();
      authModal.open('login');
    }
  };

  const handleRegister = (e: React.MouseEvent) => {
    if (authModal) {
      e.preventDefault();
      authModal.open('register');
    }
  };

  return (
    <div className="lnb2-auth max-md:hidden">
      {/* Login — plain text */}
      <Link to="/login" className="lnb2__login" onClick={handleLogin}>
        <Trans message="Log in" />
      </Link>

      {/* Hairline divider */}
      {!registration.disable && <span className="lnb2-auth-divider" aria-hidden="true" />}

      {/* Sign up — filled dark button */}
      {!registration.disable && (
        <Link to="/register" className="lnb2__cta" onClick={handleRegister}>
          <Trans message="Sign up" />
        </Link>
      )}
    </div>
  );
}

/* ------------------------------ MOBILE ---------------------------------- */
function MobileButtons() {
  const {registration} = useSettings();
  const navigate = useNavigate();
  const authModal = useAuthModal();

  const openLogin = () => {
    if (authModal) authModal.open('login');
    else navigate('/login');
  };
  const openRegister = () => {
    if (authModal) authModal.open('register');
    else navigate('/register');
  };

  return (
    <MenuTrigger>
      <IconButton size="md" className="md:hidden">
        <PersonIcon />
      </IconButton>
      <Menu>
        <Item value="login" onSelected={openLogin}>
          <Trans message="Login" />
        </Item>
        {!registration.disable && (
          <Item value="register" onSelected={openRegister}>
            <Trans message="Register" />
          </Item>
        )}
      </Menu>
    </MenuTrigger>
  );
}
